import {z} from 'zod'

import {dateOnlyFromIso, tzDateFromIso} from 'src/api/zod-helpers'
import {mapStockSource, stockSourceRawFields} from 'src/equipment/stock-source'

const billingTermsFieldsRawSchema = {
    billing_type: z.string().nullable().optional(),
    billing_rate: z.string().nullable().optional(),
    billing_terms_mixed: z.boolean().optional(),
}

const jobProvenanceFieldsRawSchema = {
    job_id: z.string().nullable().optional(),
    job_name: z.string().nullable().optional(),
}

const returnedBillingFieldsRawSchema = {
    rental_stint_ids: z.array(z.string()).optional(),
    ...jobProvenanceFieldsRawSchema,
    billing_state: z.enum(['unbilled', 'billed', 'not_billable', 'no_rental_record']).optional(),
    remaining_amount: z.coerce.number().nullable().optional(),
    charged_amount: z.coerce.number().nullable().optional(),
    invoice_id: z.string().nullable().optional(),
    invoice_count: z.number().optional(),
}

const stockRowRawSchema = z.object({
    ...stockSourceRawFields,
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    quantity: z.number(),
    location_label: z.string(),
    deployed_at: z.string(),
    job_count: z.number().nullable().optional(),
    unconfirmed_rental_stint_ids: z.array(z.string()).optional(),
    ...billingTermsFieldsRawSchema,
    ...jobProvenanceFieldsRawSchema,
})

const identifiedRowRawSchema = z.object({
    rental_asset_id: z.string(),
    asset_display_name: z.string(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    location_label: z.string(),
    deployed_at: z.string(),
    unconfirmed_rental_stint_ids: z.array(z.string()).optional(),
    ...billingTermsFieldsRawSchema,
    ...jobProvenanceFieldsRawSchema,
})

const thirdPartyRowRawSchema = identifiedRowRawSchema.extend({
    third_party_rental_company_id: z.string(),
    third_party_rental_company_name: z.string(),
})

const pendingRowBaseRawSchema = z.object({
    ...stockSourceRawFields,
    job_id: z.string(),
    job_name: z.string(),
    job_scheduled_date: z.string(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    party: z.enum(['first_party', 'third_party']),
    quantity: z.number(),
    billing_type: z.string().nullable().optional(),
    billing_rate: z.string().nullable().optional(),
    deployable: z.boolean(),
    not_ready_reason: z.string().nullable().optional(),
    third_party_rental_company_name: z.string().nullable().optional(),
})

const pendingRowRawSchema = z.discriminatedUnion('is_uniquely_identified', [
    pendingRowBaseRawSchema.extend({is_uniquely_identified: z.literal(false)}),
    pendingRowBaseRawSchema.extend({
        is_uniquely_identified: z.literal(true),
        rental_asset_id: z.string(),
        asset_display_name: z.string().nullable().optional(),
    }),
])

const returnedStockRowRawSchema = stockRowRawSchema.extend({
    returned_at: z.string(),
    ...returnedBillingFieldsRawSchema,
})

const returnedIdentifiedRowRawSchema = identifiedRowRawSchema.extend({
    returned_at: z.string(),
    ...returnedBillingFieldsRawSchema,
})

const returnedThirdPartyRowRawSchema = thirdPartyRowRawSchema.extend({
    returned_at: z.string(),
    ...returnedBillingFieldsRawSchema,
})

export const projectDeployedEquipmentRawSchema = z.object({
    stock_rows: z.array(stockRowRawSchema),
    identified_rows: z.array(identifiedRowRawSchema),
    third_party_rows: z.array(thirdPartyRowRawSchema),
    pending_rows: z.array(pendingRowRawSchema).default([]),
    returned_stock_rows: z.array(returnedStockRowRawSchema).default([]),
    returned_identified_rows: z.array(returnedIdentifiedRowRawSchema).default([]),
    returned_third_party_rows: z.array(returnedThirdPartyRowRawSchema).default([]),
})

export const stockEquipmentReturnResponseRawSchema = z.object({
    ...stockSourceRawFields,
    applied_quantity: z.number(),
    rental_asset_type_id: z.string(),
    remaining_deployed_quantity: z.number(),
})

export const identifiedEquipmentReturnResponseRawSchema = z.object({
    applied_quantity: z.number(),
    rental_asset_ids: z.array(z.string()),
})

export const equipmentDeploymentResponseRawSchema = z.object({
    event_count: z.number(),
    location_label: z.string(),
    deployed_on: z.string(),
})

const optionalReturnedOnSchema = z.object({
    returnedOn: z.date().optional(),
    returnedTime: z.string().nullable().optional(),
})

export const stockEquipmentReturnInputSchema = z
    .object({
        thirdPartyRentalAssetOfferingId: z.string().optional(),
        rentalAssetTypeId: z.string(),
        quantity: z.number().int().min(1),
        yardId: z.string().min(1),
    })
    .merge(optionalReturnedOnSchema)

export const identifiedEquipmentReturnInputSchema = z
    .object({
        rentalAssetIds: z.array(z.string()).min(1),
        yardId: z.string().min(1),
    })
    .merge(optionalReturnedOnSchema)

export const thirdPartyEquipmentReturnInputSchema = z
    .object({
        rentalAssetIds: z.array(z.string()).min(1),
        yardId: z.string().min(1),
    })
    .merge(optionalReturnedOnSchema)

export const deployProjectEquipmentInputSchema = z.object({
    jobId: z.string(),
    locationId: z.string(),
    deployedOn: z.date(),
    deployedTime: z.string().nullable().optional(),
    rentalAssetId: z.string().optional(),
    rentalAssetTypeId: z.string().optional(),
    quantity: z.number().int().min(1).optional(),
    thirdPartyRentalAssetOfferingId: z.string().optional(),
})

function mapBillingTerms(raw: {
    billing_type?: string | null
    billing_rate?: string | null
    billing_terms_mixed?: boolean
}) {
    return {
        billingType: raw.billing_type ?? null,
        billingRate: raw.billing_rate ?? null,
        billingTermsMixed: raw.billing_terms_mixed ?? false,
    }
}

function mapJobProvenance(raw: {job_id?: string | null; job_name?: string | null}) {
    return {
        jobId: raw.job_id ?? null,
        jobName: raw.job_name ?? null,
    }
}

function mapReturnedRow<
    T extends {
        location_label: string
        deployed_at: string
        returned_at: string
    } & Parameters<typeof mapBillingTerms>[0],
>(row: T) {
    return {
        locationLabel: row.location_label,
        deployedAt: tzDateFromIso.parse(row.deployed_at),
        returnedAt: tzDateFromIso.parse(row.returned_at),
        ...mapBillingTerms(row),
    }
}

export const projectDeployedEquipmentSchema = projectDeployedEquipmentRawSchema.transform(
    (raw) => ({
        stockRows: raw.stock_rows.map((row) => ({
            ...mapStockSource(row),
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            quantity: row.quantity,
            locationLabel: row.location_label,
            deployedAt: tzDateFromIso.parse(row.deployed_at),
            jobCount: row.job_count ?? null,
            unconfirmedRentalStintIds: row.unconfirmed_rental_stint_ids ?? [],
            ...mapBillingTerms(row),
            ...mapJobProvenance(row),
        })),
        identifiedRows: raw.identified_rows.map((row) => ({
            rentalAssetId: row.rental_asset_id,
            assetDisplayName: row.asset_display_name,
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            locationLabel: row.location_label,
            deployedAt: tzDateFromIso.parse(row.deployed_at),
            unconfirmedRentalStintIds: row.unconfirmed_rental_stint_ids ?? [],
            ...mapBillingTerms(row),
            ...mapJobProvenance(row),
        })),
        thirdPartyRows: raw.third_party_rows.map((row) => ({
            rentalAssetId: row.rental_asset_id,
            assetDisplayName: row.asset_display_name,
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            thirdPartyRentalCompanyId: row.third_party_rental_company_id,
            thirdPartyRentalCompanyName: row.third_party_rental_company_name,
            locationLabel: row.location_label,
            deployedAt: tzDateFromIso.parse(row.deployed_at),
            unconfirmedRentalStintIds: row.unconfirmed_rental_stint_ids ?? [],
            ...mapBillingTerms(row),
            ...mapJobProvenance(row),
        })),
        pendingRows: raw.pending_rows.map((row) => ({
            ...mapStockSource(row),
            jobId: row.job_id,
            jobName: row.job_name,
            jobScheduledDate: dateOnlyFromIso.parse(row.job_scheduled_date),
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            party: row.party,
            isUniquelyIdentified: row.is_uniquely_identified,
            quantity: row.quantity,
            billingType: row.billing_type ?? null,
            billingRate: row.billing_rate ?? null,
            deployable: row.deployable,
            ...(row.not_ready_reason ? {notReadyReason: row.not_ready_reason} : {}),
            ...(row.is_uniquely_identified
                ? {
                      rentalAssetId: row.rental_asset_id,
                      assetDisplayName: row.asset_display_name ?? null,
                  }
                : {}),
            thirdPartyRentalCompanyName: row.third_party_rental_company_name ?? null,
        })),
        returnedStockRows: raw.returned_stock_rows.map((row) => ({
            ...mapStockSource(row),
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            quantity: row.quantity,
            jobCount: row.job_count ?? null,
            ...mapReturnedRow(row),
            rentalStintIds: row.rental_stint_ids ?? [],
            ...mapJobProvenance(row),
            billingState: row.billing_state ?? 'no_rental_record',
            remainingAmount: row.remaining_amount ?? null,
            chargedAmount: row.charged_amount ?? null,
            invoiceId: row.invoice_id ?? null,
            invoiceCount: row.invoice_count ?? 0,
        })),
        returnedIdentifiedRows: raw.returned_identified_rows.map((row) => ({
            rentalAssetId: row.rental_asset_id,
            assetDisplayName: row.asset_display_name,
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            ...mapReturnedRow(row),
            rentalStintIds: row.rental_stint_ids ?? [],
            ...mapJobProvenance(row),
            billingState: row.billing_state ?? 'no_rental_record',
            remainingAmount: row.remaining_amount ?? null,
            chargedAmount: row.charged_amount ?? null,
            invoiceId: row.invoice_id ?? null,
            invoiceCount: row.invoice_count ?? 0,
        })),
        returnedThirdPartyRows: raw.returned_third_party_rows.map((row) => ({
            rentalAssetId: row.rental_asset_id,
            assetDisplayName: row.asset_display_name,
            rentalAssetTypeId: row.rental_asset_type_id,
            rentalAssetTypeName: row.rental_asset_type_name,
            thirdPartyRentalCompanyId: row.third_party_rental_company_id,
            thirdPartyRentalCompanyName: row.third_party_rental_company_name,
            ...mapReturnedRow(row),
            rentalStintIds: row.rental_stint_ids ?? [],
            ...mapJobProvenance(row),
            billingState: row.billing_state ?? 'no_rental_record',
            remainingAmount: row.remaining_amount ?? null,
            chargedAmount: row.charged_amount ?? null,
            invoiceId: row.invoice_id ?? null,
            invoiceCount: row.invoice_count ?? 0,
        })),
    }),
)

export const stockEquipmentReturnResponseSchema = stockEquipmentReturnResponseRawSchema.transform(
    (raw) => ({
        appliedQuantity: raw.applied_quantity,
        rentalAssetTypeId: raw.rental_asset_type_id,
        remainingDeployedQuantity: raw.remaining_deployed_quantity,
        ...mapStockSource(raw),
    }),
)

export const identifiedEquipmentReturnResponseSchema =
    identifiedEquipmentReturnResponseRawSchema.transform((raw) => ({
        appliedQuantity: raw.applied_quantity,
        rentalAssetIds: raw.rental_asset_ids,
    }))

export const equipmentDeploymentResponseSchema = equipmentDeploymentResponseRawSchema.transform(
    (raw) => ({
        eventCount: raw.event_count,
        locationLabel: raw.location_label,
        deployedOn: dateOnlyFromIso.parse(raw.deployed_on.slice(0, 10)),
    }),
)

export type ProjectDeployedEquipmentRaw = z.infer<typeof projectDeployedEquipmentRawSchema>
export type ProjectDeployedEquipment = z.infer<typeof projectDeployedEquipmentSchema>
export type ProjectPendingEquipmentRow = ProjectDeployedEquipment['pendingRows'][number]
export type StockEquipmentReturnInput = z.infer<typeof stockEquipmentReturnInputSchema>
export type IdentifiedEquipmentReturnInput = z.infer<typeof identifiedEquipmentReturnInputSchema>
export type ThirdPartyEquipmentReturnInput = z.infer<typeof thirdPartyEquipmentReturnInputSchema>
export type DeployProjectEquipmentInput = z.infer<typeof deployProjectEquipmentInputSchema>
